export * from './components';
export * from '@omegagrid/tree';
export * from '@omegagrid/toolbar';
export * from '@reporting/rp-app-host';

import { ComponentId } from '@omegagrid/core';
import { AppComponent, Application, Container, parseComponentId } from '@reporting/rp-app-host';
import { componentFactories, ETL_SIDEBAR_ID } from './factories';
import { BucketPathEvent, PathSegments } from '@omegagrid/bucket';
import { File, Thread } from '@reporting/rp-types/etl';
import { Sidebar } from './components';
import { ETLSearchProvider } from './searchProvider';

export class ETLApplication extends Application {

	async register(container: Container) {
		super.register(container);
		container.services.register('etl');
		container.commands.registerProvider(new ETLSearchProvider());
	}

	async init(container: Container) {
		super.init(container);

		this.host.addEventListener('path.click', async (e: BucketPathEvent) => {
			if (e.path[0].id != 'etl') return;
			this.host.openSidebarComponent('etl');
			const sidebar = (await this.host.getComponent(ETL_SIDEBAR_ID)) as Sidebar;

			const type = e.path[1].id;
			const browserId = type == 'file' ? 'files' : 'threads';
			const browser = await sidebar.browserComponentStore.get(browserId);
			if (browser.isUpdatePending) await browser.updateComplete;

			const isLeaf = e.path.length - 1 == e.index;
			let key: string;
			if (isLeaf) {
				if (type == 'file') key = `etl-file-${e.segment.id}`;
				else key = `etl-thread-${e.segment.id}`;
			} else {
				if (type == 'file') key = null;
				else key = `${type}-${e.path.slice(2, e.path.length - 1).map(p => p.id).join('-')}`;
			}

			if (key) browser.tree.highlightNode(key);
		});
	}

	async resolvePath(id: ComponentId): Promise<PathSegments> {
		const parsedId = parseComponentId(id);
		const path: PathSegments = [{id: 'etl', label: 'ETL', clickable: true}]; /*, icon: this.info.icon}];*/

		try {
			switch (parsedId.ids[0]) {
				case 'thread':
					const thread = await this.container.ms('etl').get<Thread>(`threads/${parsedId.ids[1]}`, {
						fields: ['id_thread', 'description'],
						scope: 1
					});
					if (!thread) return [];
					path.push({id: thread.type, label: 'threads', clickable: false});
					const threadPath = thread.description.split('/');
					threadPath.map((chunk, index) => path.push(index === threadPath.length - 1 ? {
						id: thread.id_thread.toString(),
						label: `${chunk} (#${thread.id_thread})`,
						clickable: true
					} : {
						id: chunk,
						label: chunk,
						clickable: true
					}));

					return path;
				case 'file':
					const file = await this.container.ms('etl').get<File>(`files/${parsedId.ids[1]}`, {
						fields: ['id_file', 'input_file']
					});

					if (!file) return [];
					path.push({id: 'file', label: 'files', clickable: false});
					const filePath = file.input_file.split('/');
					filePath.map((chunk, index) => path.push(index === filePath.length - 1 ? {
						id: file.id_file.toString(),
						label: `${chunk} (#${file.id_file})`,
						clickable: true
					} : {
						id: chunk,
						label: chunk,
						clickable: true
					}));

					return path;
				default: return [];
			}
		} catch (error) {
			return [];
		}
	}

	openPath(path: string) {
		const segments = path.split('/');
		if (segments.length == 0) return;
		switch (segments[0]) {
			case 'thread':
				this.container.host.open(`etl-thread-${segments[1]}`);
				break;
			case 'file':
				this.container.host.open(`etl-file-${segments[1]}`);
				break;
		}
	}

	createComponent(id: ComponentId): AppComponent|Promise<AppComponent> {
		const parsedId = parseComponentId(id);
		const factory = componentFactories.get(parsedId.id as string || parsedId.type);
		if (!factory) return null;
		return factory(this, parsedId);
	}

}
